// @flow
import React, { type Node, Fragment, useState, useCallback, useMemo, useEffect } from 'react';
import reduce from 'ramda/es/reduce';
import isEmpty from 'ramda/es/isEmpty';
import map from 'ramda/es/map';
import prop from 'ramda/es/prop';
import { Trans } from 'react-i18next';

import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link';
import Modal, { ModalHeader } from 'app/common/components/Modal';
import { Table, Tr, Td, Th } from 'app/common/ui-components/table';
import Picture from 'app/common/ui-next/picture';
import OverflowedText from 'app/common/components/OveflowedText';
import PlainIcon, { iconTypes } from 'app/common/ui-next/plain-icon';

import { useAssignmentsData, type AttachedFileType } from './AssignmentsData';
import FileIcon from './FileIcon';
import styles from './Attached.scss';


type Props = {
  className?: string,
  scheduleId: number,
  onAssignmentLoad: (scheduleId: number, assignmentIds: Array<number>) => void,
}

const Attached = ({ className, scheduleId, onAssignmentLoad }: Props): Node => {
  const [open, setOpen] = useState(false);
  const toggleModal = useCallback(() => {
    setOpen(state => !state);
  }, []);

  const assignments = useAssignmentsData(scheduleId);

  const attachedFiles: Array<AttachedFileType> = useMemo(() => (
    reduce((acc, { attached_files: attaches }) => ([
      ...acc,
      ...attaches,
    ]), [], assignments)
  ), [assignments]);

  useEffect(() => {
    if (!isEmpty(assignments)) {
      onAssignmentLoad(scheduleId, map(prop('id'), assignments));
    }
  }, [assignments, scheduleId]);


  if (isEmpty(assignments)) {
    return null;
  }

  return (
    <Fragment>
      <PlainButtonAsLink className={className} onClick={toggleModal}>
        <PlainIcon
          className={className}
          type={iconTypes.PAPER_CLIP}
          width={20}
          height="auto"
        />
      </PlainButtonAsLink>
      {open
        && <Modal toggleModal={toggleModal} className={styles.modal}>
          <ModalHeader>
            <Trans i18nKey="dnevnik:dnevnik_attach_modal_header">
              {'Прикрепленные файлы'}
            </Trans>
          </ModalHeader>
          <Table className={styles.table}>
            <thead>
              <Tr header className={styles.tableHeader}>
                <Th alignLeft>
                  {'№'}
                </Th>
                <Th alignLeft>
                  <Trans i18nKey="dnevnik:dnevnik_attach_modal_column2">
                    {'Файл'}
                  </Trans>
                </Th>
                <Th alignCenter>
                  <Trans i18nKey="dnevnik:dnevnik_attach_modal_column3">
                    {'Формат'}
                  </Trans>
                </Th>
                <Th>&nbsp;</Th>
              </Tr>
            </thead>
            <tbody>
              {attachedFiles.map(({
                id,
                name,
                url,
                thumb_url: thumb,
                original_filetype: fType,
                original_filename: fName,
              }, i) => (
                <Tr key={id}>
                  <Td className={styles.cellNum} alignLeft>{`${i + 1}.`}</Td>
                  <Td className={styles.cellName}>
                    <OverflowedText>{name}</OverflowedText>
                  </Td>
                  <Td className={styles.cellThumb} alignCenter>
                    {thumb
                      ? <Picture
                        className={styles.thumb}
                        alt={name}
                        src={thumb}
                        height="40px"
                        width="40px"
                      />
                      : <FileIcon
                        type={fType}
                        name={fName}
                        className={styles.icon}
                      />}
                  </Td>
                  <Td className={styles.cellLink}>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={url}
                      className={styles.downloadLink}
                      download="download"
                    >
                      <Trans i18nKey="dnevnik:dnevnik_attach_modal_download">
                        {'Просмотреть'}
                      </Trans>
                    </a>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Modal>}
    </Fragment>
  );
};

export default Attached;
